import type { Conversation } from '@/app/types/conversations';
import type {
  DocumentRegionBBox,
  DocumentSourceLinkReference,
  DocumentViewerResponse,
  DocumentViewerDocument,
  DocumentViewerVersion,
  DocumentViewerThumbnailsResponse,
} from '@/app/types/project-note';
import {
  ActionResolveData,
  BidOutcome,
  CriticalFinding,
  DashboardMetrics,
  DocumentSet,
  DocumentSetResults,
  DocumentSuggestionResult,
  Project,
  ProjectAction,
  ProjectBid,
  ProjectBidScopeAddendum,
  ProjectBidScopeAddendumMutationRequest,
  ProjectDetails,
  ProjectListItem,
  ProjectUpdateRequest,
  ProjectVendorQuoteReview,
} from '@/app/types/projects';
import { Upload, UploadRequest, UploadType, VendorQuotePackage } from '@/app/types/uploads';
import type { WalkthroughResponse } from '@/app/types/walkthrough';
import type { CriticalFindingFilters, ProjectFilters } from '@/app/hooks/query-keys';
import { ApiClient, ApiClientProps, Paginated } from './client';

export type ProjectEvidenceSearchResponse = {
  coverage: {
    blueprint_artifacts_indexed: number;
    blueprint_artifacts_total: number;
    specification_chunks_indexed: number;
    specification_chunks_total: number;
  };
  blueprints: Array<{
    result_key: string;
    artifact_id: number | null;
    title: string;
    form: string;
    sheet_number: string;
    sheet_title: string;
    filename: string;
    upload_id: number;
    page_number: number;
    bounds: DocumentRegionBBox | null;
    passages: Array<{
      text: string;
      origin: 'selected_ocr' | 'visual_reading' | 'inventory_observation' | 'lighting_fixture';
    }>;
    target: {
      kind: 'blueprint';
      upload_id: number;
      page_number: number;
      bounds: DocumentRegionBBox | null;
    };
  }>;
  specifications: Array<{
    chunk_id: number;
    project_document_id: number;
    title: string;
    section_title: string;
    filename: string;
    page_numbers: number[];
    text: string;
    target: { kind: 'specification'; project_document_id: number; page_numbers: number[] };
  }>;
  takeoff_items: Array<{
    item_id: number;
    name: string;
    package: string;
    text: string;
    matched_components: string[];
    target: { kind: 'takeoff_item'; item_id: number };
  }>;
  issues: Array<{
    issue_id: number;
    title: string;
    text: string;
    target: { kind: 'issue'; issue_id: number };
  }>;
  project_info: Array<{
    passage_id: number;
    section: 'overview' | 'documents' | 'codes';
    title: string;
    text: string;
    target: { kind: 'project_info'; section: 'overview' | 'documents' | 'codes' };
  }>;
};

export type UploadProgressHandler = (progressPercent: number) => void;

export type ProcessingBatchResponse = {
  id: number;
  kind: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  processing_duration: string | null;
  upload_count: number;
  use_manual_pricing: boolean;
  analysis: {
    uploads?: Array<{
      // Basic metadata
      upload_id: number;
      filename: string;
      parse_strategy: 'accubid_format_1' | 'llm' | null;
      type: 'csv' | 'document';
      detected_format_confidence: number | null;
      detection_attempts?: Array<{
        format_id: string;
        format_name: string;
        success: boolean;
        message: string;
      }> | null;

      // Analysis data (for CSV uploads)
      header_row?: number | null;
      csv_headers?: string[] | null;
      reasoning?: string | null;
      analyzed_at?: string | null;
      field_extractions?: Array<{
        confidence: number;
        field_name: string;
        column_indices: Array<{ name: string; index: number }>;
        extraction_instructions: string;
      }> | null;
      format_info?: {
        format_id: string;
        header_row: number;
        first_data_row: number;
        parser_class: string;
        column_mappings: Array<{
          field_name: string;
          column_index: number;
          column_name: string | null;
        }>;
      } | null;
      instructions?: Array<{
        name: string;
        title: string;
        description: string;
        prompts: string[];
      }>;
    }> | null;
  } | null;
};

export type BlueprintProcessingBatchResponse = {
  id: number;
  kind: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  processing_duration: string | null;
  upload_count: number;
  use_manual_pricing: boolean;
  analysis: {
    uploads?: Array<{
      upload_id: number;
      filename: string;
      summary: {
        total_pages: number;
        completed_pages: number;
        electrical_pages: number;
        valid_blueprints: number;
      };
      pages: Array<{
        page_number: number;
        is_valid_blueprint: boolean;
        is_matched: boolean;
        sheet_number: string | null;
        ocr_source: 'paddleocr' | 'gemini' | null;
        status: string;
        s3_path: string | null;
        extracted_text?: {
          paddle_ocr: string | null;
          paddle_ocr_lines: Array<{ text: string; confidence: number }> | null;
          gemini: string | null;
        };
        error_type?: string;
        notes_count?: number;
        tables_count?: number;
        notes_by_category?: Record<string, number>;
      }>;
    }>;
  } | null;
};

export type BlueprintProcessingStage =
  | 'checking_changes'
  | 'awaiting_review'
  | 'preparing_pages'
  | 'rendering_pages'
  | 'reading_pages'
  | 'generating_page_manifests'
  | 'organizing_documents'
  | 'resolving_document_lifecycle'
  | 'chunking_content'
  | 'generating_embeddings'
  | 'persisting_embeddings'
  | 'organizing_details'
  | 'generating_project_overview'
  | 'finishing'
  | 'evaluating'
  | 'processing';

export type BlueprintProcessingProgressResponse = {
  is_terminal: boolean;
  stage: BlueprintProcessingStage;
  completed: number;
  total: number;
};

export type CorpusArtifact = {
  key: string;
  form: 'schedule' | 'diagram' | 'plan' | 'legend' | 'notes' | 'detail' | 'matrix' | 'index';
  title: string;
  sheet_number: string;
  sheet_title: string;
  filename: string;
  page_number: number;
  assets: Array<{
    key: string;
    kind: string;
    label: string;
  }>;
  blocks: Array<{
    group: string;
    text: string;
    source_links: DocumentSourceLinkReference[];
  }>;
};

export type ProjectCorpus = {
  systems: Array<{
    key: string;
    label: string;
    available_views: Array<'scope' | 'sources'>;
    default_view: 'scope' | 'sources';
    current_design_stage_id: number | null;
    design_stages: Array<{
      id: number | null;
      stage: 'schematic_design' | 'design_development' | 'construction_documents' | null;
      stage_percentage: number | null;
      artifacts: CorpusArtifact[];
      scope_status: 'idle' | 'queued' | 'processing' | 'completed' | 'failed';
      scope: {
        items: Array<{
          group: string;
          text: string;
          source_links: DocumentSourceLinkReference[];
        }>;
      } | null;
    }>;
  }>;
};

/**
 * API client for projects-related endpoints
 */
export type InventorySource = {
  highlight_bounds: { left: number; top: number; right: number; bottom: number } | null;
  artifact_id: number;
  role: 'direct' | 'context';
  title: string;
  bounds: { left: number; top: number; right: number; bottom: number } | null;
  upload_id: number;
  page_number: number;
  blueprint_page_id: number;
  sheet_number: string;
  sheet_title: string;
  filename: string;
};
export type InventoryCandidate = {
  id: number;
  reader_key: string;
  decision: 'identified' | 'excluded' | 'needs_review';
  reason: string;
  review_state: 'pending' | 'resolved' | 'dismissed';
  source: InventorySource;
};
export type InventoryObservation = {
  origin: 'extraction' | 'review';
  supersedes_id: number | null;
  id: number;
  reader_key: string;
  entity_kind: string;
  printed_name: string | null;
  tag: string | null;
  payload: Record<string, unknown>;
  rejected: boolean;
  item_ids: number[];
  sources: InventorySource[];
};
export type InventoryItem = {
  discrepancies: { statement: string; observation_ids: number[] }[];
  id: number;
  label: string | null;
  entity_kind: string;
  review_state: 'pending' | 'approved' | 'rejected';
  observation_ids: number[];
};
export type InventorySystem = {
  key: string;
  label: string;
  inventory_id: number;
  item_count: number;
  items: InventoryItem[];
  observations: InventoryObservation[];
  entity_kinds: string[];
  display_fields: string[];
  review_findings: string[];
  reader_keys: string[];
  candidates: InventoryCandidate[];
};
export type InventoryCommand = InventoryReview extends infer T
  ? T extends InventoryReview
    ? Omit<T, 'inventory_id'>
    : never
  : never;

export type ProjectInventory = {
  document_set_revision_id: number | null;
  systems: InventorySystem[];
};
export type InventoryReview = { inventory_id: number } & (
  | {
      action: 'record_occurrence';
      reader_key: string;
      artifact_id: number;
      highlight_bounds: NonNullable<InventorySource['bounds']>;
      printed_name: string | null;
      tag: string | null;
      entity_kind: string;
      item_id: number | null;
      candidate_id: number | null;
      supersedes_id: number | null;
    }
  | { action: 'dismiss_candidate'; candidate_id: number; dismissed: boolean }
  | { action: 'edit'; item_id: number; label: string | null; entity_kind: string }
  | { action: 'review'; item_id: number; state: InventoryItem['review_state'] }
  | { action: 'merge'; item_ids: number[]; label: string | null; entity_kind: string }
  | {
      action: 'split';
      item_id: number | null;
      observation_ids: number[];
      label: string | null;
      entity_kind: string;
    }
  | { action: 'assign'; observation_id: number; item_ids: number[] }
  | { action: 'reject_observation'; observation_id: number; rejected: boolean }
);

export type ReviewPage = {
  id: number | null;
  upload_id: number;
  number: number;
  sheet_number: string;
  title: string;
  issue: string;
  image: string;
};
export type RevisionChange = {
  source: string;
  target: string | null;
  effect: 'add' | 'full' | 'partial' | 'skip' | 'unknown';
  reason?: string;
  matches: Array<{
    page: number;
    target: number | 'add' | 'skip' | 'unknown';
    current?: number | null;
    reason?: string;
  }> | null;
  shared: boolean;
};
export type DocumentReview = {
  id: number;
  status: 'preparing' | 'ready' | 'failed' | 'cancelled';
  document_set_id?: number;
  documents?: Array<{
    key: string;
    title: string;
    form: string;
    upload_ids: number[];
    source_ids: number[];
    pages: ReviewPage[];
    page_count: number;
    cover: string;
    shared: boolean;
    incoming: boolean;
    checked: boolean;
    issues?: string[];
  }>;
  changes?: RevisionChange[];
  stages?: Array<{ id: number; label: string; upload_ids: number[] }>;
};

export class ProjectsClient extends ApiClient {
  // Project-only entry points resolve the default; a project page binds its selected set.
  private async selectedSetId(projectId: number): Promise<number> {
    if (this.documentSetId !== undefined) return this.documentSetId;
    const project = await this.getById(projectId);
    return project.document_set_id;
  }

  saveDocumentSet(
    projectId: number,
    data: { stage: string; stage_percentage: number | null },
    id?: number,
  ) {
    const path = `projects/${projectId}/document-sets/`;
    return id
      ? this._patch<DocumentSet>(path, {
          ...data,
          document_set_id: id,
        })
      : this._post<DocumentSet>(path, data);
  }
  async getDocumentSetResults(projectId: number) {
    return this._get<DocumentSetResults>(`projects/${projectId}/document-set-results/`, {
      document_set_id: await this.selectedSetId(projectId),
    });
  }
  async refreshDocumentSet(projectId: number) {
    return this._post(`projects/${projectId}/document-set-results/`, {
      document_set_id: await this.selectedSetId(projectId),
    });
  }
  prepareStagedUpload(file: File) {
    return this._post<{ upload_token: string; signed_url: string }>('projects/staged-uploads/', {
      filename: file.name,
      content_type: file.type,
    });
  }

  suggestDocument(
    projectId: number | undefined,
    uploadToken: string,
    includeProjectDetails: boolean,
  ) {
    return this._post<DocumentSuggestionResult>(
      projectId === undefined
        ? 'projects/document-suggestion/'
        : `projects/${projectId}/document-suggestion/`,
      {
        upload_token: uploadToken,
        ...(includeProjectDetails ? { include_project_details: true } : {}),
      },
    );
  }

  async getDocumentReview(projectId: number) {
    return this._get<DocumentReview | null>(`projects/${projectId}/document-review/`, {
      document_set_id: await this.selectedSetId(projectId),
    });
  }
  async discardDocumentReview(projectId: number, id: number) {
    return this._post(`projects/${projectId}/document-review/`, {
      id,
      discard: true,
      document_set_id: await this.selectedSetId(projectId),
    });
  }
  async checkReviewSheets(
    projectId: number,
    id: number,
    changes: RevisionChange[],
    source: string,
  ) {
    return this._post(`projects/${projectId}/document-review/`, {
      id,
      changes,
      check_sheets: source,
      document_set_id: await this.selectedSetId(projectId),
    });
  }
  async applyDocumentReview(projectId: number, id: number, changes: RevisionChange[]) {
    return this._post(`projects/${projectId}/document-review/`, {
      id,
      changes,
      document_set_id: await this.selectedSetId(projectId),
    });
  }

  async getAuditExport(projectId: number, kind: ProjectAuditExportKind) {
    return this._get<{ filename: string; content: Record<string, unknown> }>(
      `projects/${projectId}/audit-export/`,
      { kind, document_set_id: await this.selectedSetId(projectId) },
    );
  }

  async getConversations(projectId: number, page: number) {
    return this._get<Paginated<Conversation>>(`projects/${projectId}/chats/`, {
      document_set_id: await this.selectedSetId(projectId),
      page,
      page_size: 20,
    });
  }

  async getWalkthrough(projectId: number) {
    return this._get<WalkthroughResponse>(
      `projects/${projectId}/walkthrough/?document_set_id=${await this.selectedSetId(projectId)}`,
    );
  }

  constructor(props: ApiClientProps) {
    super(props);
  }

  /**
   * Fetch all projects for the current user
   * @throws {Error} If the API request fails
   */
  async getAll(props?: ProjectFilters) {
    return this._get<Paginated<ProjectListItem>>('projects/', props);
  }

  getMetrics(scope: 'team' | 'me') {
    return this._get<DashboardMetrics>('projects/metrics/', { scope });
  }

  getCriticalFindings(filters: CriticalFindingFilters) {
    return this._get<Paginated<CriticalFinding>>('projects/critical-findings/', filters);
  }

  submitBid(id: number, bidAmount?: string) {
    return this._post<ProjectBid>(
      `projects/${id}/bid-submission/`,
      bidAmount === undefined ? {} : { bid_amount: bidAmount },
    );
  }
  unsubmitBid(id: number) {
    return this._delete(`projects/${id}/bid-submission/`);
  }
  recordBidOutcome(id: number, outcome: BidOutcome) {
    return this._post<ProjectBid>(`projects/${id}/bid-outcome/`, { bid_outcome: outcome });
  }
  undoBidOutcome(id: number) {
    return this._delete(`projects/${id}/bid-outcome/`);
  }
  deferBidOutcome(id: number) {
    return this._post<ProjectBid>(`projects/${id}/bid-outcome-deferral/`, {});
  }

  /**
   * Fetch a single project by ID
   * @throws {Error} If the API request fails
   */
  async getById(id: number, context?: { document_set_id?: number; takeoff_id?: number }) {
    return this._get<ProjectDetails>(
      `projects/${id}/`,
      context ?? (this.documentSetId ? { document_set_id: this.documentSetId } : {}),
    );
  }

  async getInventoryArtifacts(id: number, query: string) {
    return this._get<{ artifacts: InventorySource[] }>(
      `projects/${id}/inventory/?document_set_id=${await this.selectedSetId(id)}&artifact_query=${encodeURIComponent(query)}`,
    );
  }

  async getInventoryArtifact(id: number, artifactId: number) {
    return this._get<{ source: InventorySource; image_url: string }>(
      `projects/${id}/inventory/?document_set_id=${await this.selectedSetId(id)}&artifact_id=${artifactId}`,
    );
  }

  async getInventory(id: number) {
    return this._get<ProjectInventory>(
      `projects/${id}/inventory/?document_set_id=${await this.selectedSetId(id)}`,
    );
  }

  async reviewInventory(id: number, command: InventoryReview) {
    return this._post<ProjectInventory>(
      `projects/${id}/inventory/?document_set_id=${await this.selectedSetId(id)}`,
      command,
    );
  }

  async getCorpus(id: number) {
    return this._get<ProjectCorpus>(
      `projects/${id}/extraction-corpus/?document_set_id=${await this.selectedSetId(id)}`,
    );
  }

  /**
   * Create a new project
   * @throws {Error} If the API request fails
   */
  async create(data: {
    address?: string;
    bid_due_date?: string;
    initial_stage?: { stage: string; stage_percentage: number | null } | undefined;
    name: string;
    use_manual_pricing?: boolean | undefined;
    use_spec_for_material_selection?: boolean | undefined;
    building_type?: Project['building_type'] | undefined;
    product_catalog_id?: number | undefined;
    product_catalog_ids?: number[] | undefined;
    skip_upload_requirement?: boolean | undefined;
    accubid_source?: { database_token: string; estimate_id: string } | undefined;
  }) {
    return this._post<Project>('projects/', data);
  }

  /**
   * Delete a project by ID
   * @throws {Error} If the API request fails
   */
  async delete(id: number) {
    return this._delete(`projects/${id}/`);
  }

  async update(id: number, data: ProjectUpdateRequest) {
    return this._patch<ProjectDetails>(
      `projects/${id}/${this.documentSetId ? `?document_set_id=${this.documentSetId}` : ''}`,
      data,
    );
  }

  /**
   * Create an upload record and get a signed URL for S3 upload
   * @param projectId - Project ID
   * @param uploadType - Type of upload (document, blueprint, takeoff)
   * @param file - File to upload
   * @throws {Error} If the API request fails
   * @returns Upload object with signed URL for S3 upload
   */
  async prepareUpload(
    projectId: number,
    uploadType: UploadType,
    file: File,
    options: { vendorQuotePackage?: VendorQuotePackage; isShared?: boolean } = {},
  ) {
    const requestData: UploadRequest = {
      is_shared: options.isShared ?? false,
      filename: file.name,
      upload_type: uploadType,
      content_type: file.type,
    };
    if (options.vendorQuotePackage) {
      requestData.processing_hints = {
        profile: 'vendor_quote',
        parameters: { package: options.vendorQuotePackage },
      };
    }

    return this._post<Upload>(
      `projects/${projectId}/uploads/?document_set_id=${await this.selectedSetId(projectId)}`,
      requestData,
    );
  }

  async attachUpload(
    projectId: number,
    uploadType: UploadType,
    uploadToken: string,
    options: { isShared?: boolean } = {},
  ) {
    return this._post<Omit<Upload, 'signed_url'>>(
      `projects/${projectId}/uploads/?document_set_id=${await this.selectedSetId(projectId)}`,
      { upload_type: uploadType, upload_token: uploadToken, is_shared: options.isShared ?? false },
    );
  }

  /**
   * Upload a file to S3 using a signed URL
   * @throws {Error} If the upload fails
   */
  async uploadToS3(
    signedUrl: string,
    file: File,
    onProgress?: UploadProgressHandler,
  ): Promise<Response | void> {
    if (typeof XMLHttpRequest === 'undefined') {
      const response = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('S3 upload failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          url: signedUrl,
        });
        throw new Error(`Failed to upload file to S3: ${response.statusText} - ${errorText}`);
      }

      onProgress?.(100);
      return response;
    }

    await new Promise<void>((resolve, reject) => {
      const fail = (message: string) =>
        reject(new Error(`Failed to upload file to S3: ${message}`));

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', signedUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.upload.addEventListener('progress', (event) => {
        if (onProgress && event.lengthComputable && event.total > 0) {
          onProgress(Math.min(100, Math.max(0, Math.round((event.loaded / event.total) * 100))));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve();
          return;
        }

        const errorText = xhr.responseText || 'Unknown error';
        console.error('S3 upload failed:', {
          status: xhr.status,
          statusText: xhr.statusText,
          error: errorText,
          url: signedUrl,
        });
        fail(`${xhr.statusText} - ${errorText}`);
      });

      xhr.addEventListener('error', () => fail('network error'));
      xhr.addEventListener('abort', () => fail('upload aborted'));

      xhr.send(file);
    });

    return undefined;
  }

  /**
   * Get all uploads for a project
   * @throws {Error} If the API request fails
   */
  async getUploads(projectId: number) {
    return this._get<Paginated<Upload>>(
      `projects/${projectId}/uploads/?document_set_id=${await this.selectedSetId(projectId)}`,
    );
  }

  /**
   * Process all uploads for a project
   * @throws {Error} If the API request fails
   */
  async processUploads(projectId: number, uploadIds: number[], reviewBeforeApply = false) {
    return this._post<{ message: string; upload_count: number }>(
      `projects/${projectId}/uploads/process/`,
      {
        upload_ids: uploadIds,
        document_set_id: await this.selectedSetId(projectId),
        review_before_apply: reviewBeforeApply,
      },
    );
  }

  /**
   * Get presigned download URL for an upload
   * @throws {Error} If the API request fails
   */
  async getUploadDownloadUrl(
    projectId: number,
    uploadId: number,
    inline = false,
  ): Promise<{ url: string }> {
    return this._get<{ url: string }>(
      `projects/${projectId}/uploads/${uploadId}/download-url/${inline ? '?inline=true' : ''}`,
    );
  }

  /**
   * Get processing batch debug information for a project (takeoff ingestion)
   * @throws {Error} If the API request fails
   */
  async getProcessingBatch(projectId: number, batchKind: string = 'takeoff_ingest') {
    return this._get<ProcessingBatchResponse>(
      `projects/${projectId}/processing/${batchKind}/?document_set_id=${await this.selectedSetId(projectId)}`,
    );
  }

  /**
   * Get blueprint processing batch debug information for a project
   * @throws {Error} If the API request fails
   */
  async getBlueprintProcessingBatch(projectId: number) {
    return this._get<BlueprintProcessingBatchResponse>(
      `projects/${projectId}/processing/vision_ingest/?document_set_id=${await this.selectedSetId(projectId)}`,
    );
  }

  async getBlueprintProcessingProgress(projectId: number) {
    return this._get<BlueprintProcessingProgressResponse>(
      `projects/${projectId}/processing/vision_ingest/progress/?document_set_id=${await this.selectedSetId(projectId)}`,
    );
  }

  async searchEvidence(projectId: number, query: string, takeoffId?: number) {
    return this._get<ProjectEvidenceSearchResponse>(`projects/${projectId}/evidence-search/`, {
      q: query,
      mode: 'hybrid',
      ...(takeoffId
        ? { takeoff_id: takeoffId }
        : { document_set_id: await this.selectedSetId(projectId) }),
    });
  }

  async getDocumentsViewer(
    projectId: number,
    params: {
      sourceKind?: 'blueprint' | 'document';
      uploadId?: number;
      projectDocumentId?: number;
      pageNumber?: number;
      regionId?: number;
      regionKind?: 'note' | 'table' | 'diagram';
      includePageData?: boolean;
      includeDocuments?: boolean;
      followReplacements?: boolean;
    } = {},
    options?: { silentStatuses?: number[] },
  ) {
    const query: Record<string, string | number> = {
      document_set_id: await this.selectedSetId(projectId),
    };
    if (params.sourceKind !== undefined) query['source_kind'] = params.sourceKind;
    if (params.uploadId !== undefined) query['upload_id'] = params.uploadId;
    if (params.projectDocumentId !== undefined) {
      query['project_document_id'] = params.projectDocumentId;
    }
    if (params.pageNumber !== undefined) query['page_number'] = params.pageNumber;
    if (params.regionId !== undefined) query['region_id'] = params.regionId;
    if (params.regionKind !== undefined) query['region_kind'] = params.regionKind;
    if (params.includePageData) query['include_page_data'] = 1;
    if (params.includeDocuments === false) query['include_documents'] = 0;
    if (params.followReplacements) query['follow_replacements'] = 1;

    return this._get<DocumentViewerResponse>(
      `projects/${projectId}/documents-viewer/`,
      Object.keys(query).length > 0 ? query : undefined,
      'v1',
      options,
    );
  }

  async getDocumentsViewerCatalog(projectId: number) {
    return this._get<DocumentViewerDocument[]>(`projects/${projectId}/documents-viewer/catalog/`, {
      document_set_id: await this.selectedSetId(projectId),
    });
  }

  async getDocumentsViewerHistory(projectId: number, uploadId: number, pageNumber: number) {
    return this._get<DocumentViewerVersion[]>(`projects/${projectId}/documents-viewer/history/`, {
      document_set_id: await this.selectedSetId(projectId),
      upload_id: uploadId,
      page_number: pageNumber,
    });
  }

  async getDocumentsViewerThumbnails(
    projectId: number,
    params: {
      sourceKind: 'blueprint' | 'document';
      uploadId?: number;
      projectDocumentId?: number;
      pageNumbers?: number[];
    },
    options?: { silentStatuses?: number[] },
  ) {
    const query: Record<string, string | number | number[]> = {
      source_kind: params.sourceKind,
      document_set_id: await this.selectedSetId(projectId),
    };
    if (params.uploadId !== undefined) query['upload_id'] = params.uploadId;
    if (params.projectDocumentId !== undefined) {
      query['project_document_id'] = params.projectDocumentId;
    }
    if (params.pageNumbers?.length) query['page_numbers'] = params.pageNumbers;

    return this._get<DocumentViewerThumbnailsResponse>(
      `projects/${projectId}/documents-viewer/thumbnails/`,
      query,
      'v1',
      options,
    );
  }

  /**
   * Get bid scope addendum for a takeoff
   * @throws {Error} If the API request fails
   */
  async getBidScopeAddendum(projectId: number, takeoffId: number) {
    return this._get<ProjectBidScopeAddendum>(
      `projects/${projectId}/takeoffs/${takeoffId}/bid-scope-addendum/`,
    );
  }

  /**
   * Get vendor quote review for a takeoff
   * @throws {Error} If the API request fails
   */
  async getVendorQuoteReview(
    projectId: number,
    takeoffId: number,
    options?: { silentStatuses?: number[] },
  ) {
    return this._get<ProjectVendorQuoteReview>(
      `projects/${projectId}/takeoffs/${takeoffId}/vendor-quotes/`,
      undefined,
      'v1',
      options,
    );
  }

  /**
   * Hide a vendor quote and its document from the project
   * @throws {Error} If the API request fails
   */
  async saveQuoteResponses(
    projectId: number,
    takeoffId: number,
    quoteId: number,
    data: import('@/app/types/quote-purchase').PurchaseResponseUpdate,
  ) {
    return this._put<import('@/app/types/quote-purchase').PurchaseReview>(
      `projects/${projectId}/takeoffs/${takeoffId}/vendor-quotes/${quoteId}/responses/`,
      data,
    );
  }

  async retryVendorQuote(projectId: number, takeoffId: number, projectDocumentId: number) {
    return this._post<void>(
      `projects/${projectId}/takeoffs/${takeoffId}/vendor-quotes/documents/${projectDocumentId}/retry/`,
    );
  }

  async removeVendorQuote(projectId: number, takeoffId: number, projectDocumentId: number) {
    return this._delete(
      `projects/${projectId}/takeoffs/${takeoffId}/vendor-quotes/documents/${projectDocumentId}/`,
    );
  }

  async uploadVendorQuotes(projectId: number, groups: File[][]) {
    const body = new FormData();
    let index = 0;
    const indices = groups.map((files) =>
      files.map((file) => {
        body.append('files', file);
        return index++;
      }),
    );
    body.append('groups', JSON.stringify(indices));
    return this._post<{
      upload_ids: number[];
      duplicates: { upload_id: number; filename: string }[];
    }>(
      `projects/${projectId}/uploads/vendor-quotes/?document_set_id=${await this.selectedSetId(projectId)}`,
      body,
    );
  }

  /**
   * Regenerate bid scope addendum for a takeoff
   * @throws {APIError} If the API request fails (including 429 rate limit)
   */
  async regenerateBidScopeAddendum(projectId: number, takeoffId: number) {
    return this._post<{ status: string; message: string }>(
      `projects/${projectId}/takeoffs/${takeoffId}/bid-scope-addendum/regenerate/`,
      {},
    );
  }

  /**
   * Apply a targeted bid scope addendum edit for a takeoff
   * @throws {Error} If the API request fails
   */
  async updateBidScopeAddendum(
    projectId: number,
    takeoffId: number,
    data: ProjectBidScopeAddendumMutationRequest,
  ) {
    return this._patch<ProjectBidScopeAddendum>(
      `projects/${projectId}/takeoffs/${takeoffId}/bid-scope-addendum/`,
      data,
    );
  }

  /**
   * Get presigned URL for blueprint page
   * @throws {APIError} If the API request fails
   */
  async getBlueprintPageUrl(
    projectId: number,
    uploadId: number,
    pageNumber: number,
  ): Promise<{ url: string }> {
    return this._get<{ url: string }>(
      `projects/${projectId}/blueprint-uploads/${uploadId}/pages/${pageNumber}/signed-url/?document_set_id=${await this.selectedSetId(projectId)}`,
    );
  }

  actions = {
    skip: async (projectId: number, actionId: number) => {
      return this._post<ProjectAction>(`projects/${projectId}/actions/${actionId}/skip/`);
    },
    resolve: async (projectId: number, actionId: number, data: ActionResolveData) => {
      return this._post<ProjectAction>(`projects/${projectId}/actions/${actionId}/resolve/`, data);
    },
  };
}

export type ProjectAuditExportKind = 'extractions' | 'inventory';
